import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Cookie, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCookieConsent, type CookiePreferences } from '@/contexts/CookieConsentContext'
import { LocalizedLink } from './LocalizedLink'

function CookieToggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: () => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={cn(
        'relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200',
        checked ? 'bg-maia' : 'bg-cord',
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200',
          checked ? 'translate-x-5' : 'translate-x-0.5',
        )}
      />
    </button>
  )
}

export function CookieConsent() {
  const { t } = useTranslation()
  const { showBanner, preferences, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPreferences, setShowPreferences] = useState(false)
  const [tempPrefs, setTempPrefs] = useState<CookiePreferences>(preferences)

  useEffect(() => {
    const handleOpen = () => {
      setTempPrefs(preferences)
      setShowPreferences(true)
    }
    window.addEventListener('openCookiePreferences', handleOpen)
    return () => window.removeEventListener('openCookiePreferences', handleOpen)
  }, [preferences])

  const openPreferences = () => {
    setTempPrefs(preferences)
    setShowPreferences(true)
  }

  if (!showBanner && !showPreferences) return null

  return (
    <>
      {/* Banner */}
      {showBanner && !showPreferences && (
        <div
          className={cn(
            'fixed bottom-4 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:max-w-sm',
            'bg-cream border border-cord rounded-[28px]',
            'shadow-2xl shadow-ink/10',
            'p-5',
            'animate-in slide-in-from-bottom-5 fade-in duration-300',
          )}
          style={{ zIndex: 9999 }}
          role="dialog"
          aria-live="polite"
          aria-label={t('cookie.title', 'Cookie consent')}
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-maia-light flex items-center justify-center text-maia">
              <Cookie className="w-5 h-5" aria-hidden="true" />
            </div>
            <div>
              <h2 className="font-display text-base font-semibold text-ink leading-tight">
                {t('cookie.title', 'Cookie consent')}
              </h2>
              <p className="text-sm text-stone mt-1 leading-relaxed">
                {t('cookie.message')}{' '}
                <LocalizedLink
                  to="privacy"
                  className="font-medium underline underline-offset-2 hover:text-maia-dark transition-colors text-maia-dark"
                >
                  {t('cookie.learnMore')}
                </LocalizedLink>
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={acceptAll}
              className="flex-1 py-2.5 px-4 text-sm font-semibold rounded-full transition-colors bg-maia text-white hover:bg-maia-dark"
            >
              {t('cookie.acceptAll', 'Accetta tutti')}
            </button>
            <button
              onClick={rejectAll}
              className="flex-1 py-2.5 px-4 text-sm font-semibold rounded-full border border-cord text-stone hover:bg-maia-light transition-colors"
            >
              {t('cookie.rejectAll', 'Rifiuta')}
            </button>
          </div>

          <button
            onClick={openPreferences}
            className="mt-3 w-full text-xs font-medium text-muted hover:text-maia-dark transition-colors"
          >
            {t('cookie.customize', 'Personalizza')}
          </button>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-ink/30 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          style={{ zIndex: 10000 }}
          onClick={() => setShowPreferences(false)}
        >
          <div
            className={cn(
              'bg-cream border border-cord rounded-[28px] shadow-2xl shadow-ink/10',
              'max-w-md w-full max-h-[90vh] overflow-y-auto',
              'animate-in zoom-in-95 duration-200',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-display text-lg font-semibold text-ink">
                  {t('cookie.preferencesTitle', 'Preferenze Cookie')}
                </h2>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="p-1.5 text-muted hover:text-ink transition-colors rounded-full hover:bg-silver"
                  aria-label={t('aria.close', 'Chiudi')}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="text-sm text-stone mb-6 leading-relaxed">
                {t('cookie.preferencesDescription', 'Gestisci le tue preferenze sui cookie.')}
              </p>

              <div className="space-y-3">
                {/* Necessary */}
                <div className="flex items-center justify-between gap-4 p-4 bg-white border border-cord/50 rounded-2xl">
                  <div>
                    <p className="font-semibold text-sm text-ink">
                      {t('cookie.necessary', 'Necessari')}
                    </p>
                    <p className="text-xs text-muted mt-0.5">
                      {t('cookie.necessaryDesc', 'Essenziali per il funzionamento del sito.')}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-maia-dark bg-maia-light px-2.5 py-1 rounded-full">
                    {t('cookie.alwaysOn', 'Sempre attivi')}
                  </span>
                </div>

                {/* Analytics */}
                <div className="flex items-center justify-between gap-4 p-4 bg-white border border-cord/50 rounded-2xl">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-ink">
                      {t('cookie.analytics', 'Analitici')}
                    </p>
                    <p className="text-xs text-muted mt-0.5">
                      {t('cookie.analyticsDesc', 'Ci aiutano a capire come usi il sito.')}
                    </p>
                  </div>
                  <CookieToggle
                    checked={tempPrefs.analytics}
                    onChange={() => setTempPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                    label={t('cookie.analytics', 'Analitici')}
                  />
                </div>

                {/* Marketing */}
                <div className="flex items-center justify-between gap-4 p-4 bg-white border border-cord/50 rounded-2xl">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-ink">
                      {t('cookie.marketing', 'Marketing')}
                    </p>
                    <p className="text-xs text-muted mt-0.5">
                      {t('cookie.marketingDesc', 'Per contenuti e pubblicità personalizzati.')}
                    </p>
                  </div>
                  <CookieToggle
                    checked={tempPrefs.marketing}
                    onChange={() => setTempPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                    label={t('cookie.marketing', 'Marketing')}
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-2 justify-end">
                <button
                  onClick={() => setShowPreferences(false)}
                  className="py-2.5 px-5 text-sm font-medium text-stone hover:text-ink hover:bg-silver rounded-full transition-colors"
                >
                  {t('cookie.cancel', 'Annulla')}
                </button>
                <button
                  onClick={() => {
                    savePreferences(tempPrefs)
                    setShowPreferences(false)
                  }}
                  className="py-2.5 px-5 text-sm font-semibold rounded-full transition-colors bg-ink text-white hover:bg-stone"
                >
                  {t('cookie.savePreferences', 'Salva preferenze')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
